import { Routes } from '@angular/router';
import { BackofficeShell } from './shell/backoffice-shell/backoffice-shell';
import { OnboardingShell } from './shell/onboarding-shell/onboarding-shell';
import { authGuard } from './core/guards/auth-guard';
import { backofficeRoleGuard } from './core/guards/backoffice-role-guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes-guard';
import { environment } from '../environments/environment';

export const routes: Routes = [
  {
    path: 'session-expired',
    loadComponent: () =>
      import('./pages/session-expired/session-expired').then((m) => m.SessionExpired),
  },
  {
    path: 'workspace-suspended',
    loadComponent: () =>
      import('./pages/workspace-suspended/workspace-suspended').then((m) => m.WorkspaceSuspended),
  },
  {
    path: 'properties/onboarding',
    data: { permissions: ['PROPERTY_CREATE'] },
    component: OnboardingShell,
    canActivate: [authGuard, backofficeRoleGuard],
    canActivateChild: [authGuard, backofficeRoleGuard],
    children: [
      {
        path: '',
        data: { permissions: ['PROPERTY_CREATE'] },
        loadComponent: () => import('./pages/properties/properties').then((m) => m.Properties),
      },
    ],
  },
  {
    path: 'properties/onboarding-complete',
    data: { permissions: ['PROPERTY_VIEW'] },
    component: OnboardingShell,
    canActivate: [authGuard, backofficeRoleGuard],
    canActivateChild: [authGuard, backofficeRoleGuard],
    children: [
      {
        path: '',
        data: { permissions: ['PROPERTY_VIEW'] },
        loadComponent: () =>
          import('./pages/properties/onboarding-complete/onboarding-complete')
            .then((m) => m.OnboardingComplete),
      },
    ],
  },
  {
    path: '',
    component: BackofficeShell,
    canActivate: [authGuard, backofficeRoleGuard],
    canActivateChild: [authGuard, backofficeRoleGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'organizations',
        data: { ownerOnly: true },
        loadComponent: () =>
          import('./pages/organizations/organizations').then((m) => m.Organizations),
      },
      {
        path: 'owners',
        data: { pmWorkspaceOnly: true, permissions: ['OWNER_VIEW'] },
        loadComponent: () => import('./pages/owners/owners').then((m) => m.Owners),
      },
      {
        path: 'properties/add',
        data: { permissions: ['PROPERTY_CREATE'] },
        loadComponent: () => import('./pages/properties/properties').then((m) => m.Properties),
      },
      {
        path: 'properties/:propertyId/units/add',
        data: { permissions: ['UNIT_CREATE'] },
        loadComponent: () => import('./pages/properties/properties').then((m) => m.Properties),
      },
      {
        path: 'properties/:propertyId',
        data: { permissions: ['PROPERTY_VIEW'] },
        loadComponent: () =>
          import('./pages/property-detail/property-detail').then((m) => m.PropertyDetail),
      },
      {
        path: 'properties',
        data: { permissions: ['PROPERTY_VIEW'] },
        loadComponent: () =>
          import('./pages/property-management/property-management').then(
            (m) => m.PropertyManagement,
          ),
      },
      {
        path: 'staff',
        data: { pmWorkspaceOnly: true, ownerOnly: true, permissions: ['MEMBERSHIP_VIEW'] },
        loadComponent: () => import('./pages/staff/staff').then((m) => m.Staff),
      },
      {
        path: 'tenants',
        data: { permissions: ['TENANT_VIEW'] },
        loadComponent: () => import('./pages/tenants/tenants').then((m) => m.Tenants),
      },
      {
        path: 'applications',
        data: { permissions: ['APPLICATION_VIEW'] },
        loadComponent: () => import('./pages/applications/applications').then((m) => m.Applications),
      },
      {
        path: 'contacts',
        data: { permissions: ['TENANT_VIEW'] },
        loadComponent: () => import('./pages/contacts/contacts').then((m) => m.Contacts),
      },
      {
        path: 'leases/templates',
        data: { permissions: ['LEASE_VIEW'] },
        loadComponent: () =>
          import('./pages/leases/lease-templates/lease-templates').then((m) => m.LeaseTemplates),
      },
      {
        path: 'leases',
        data: { permissions: ['LEASE_VIEW'] },
        loadComponent: () => import('./pages/leases/leases').then((m) => m.Leases),
      },
      {
        path: 'transactions',
        data: { permissions: ['PAYMENT_VIEW'] },
        loadComponent: () =>
          import('./pages/transactions/transactions').then((m) => m.Transactions),
      },
      {
        path: 'accounting',
        data: { permissions: ['ACCOUNTING_READ'] },
        loadComponent: () => import('./pages/accounting/accounting').then((m) => m.Accounting),
      },
      {
        path: 'balances',
        data: { permissions: ['PAYMENT_VIEW', 'LEASE_VIEW'] },
        loadComponent: () => import('./pages/balances/balances').then((m) => m.Balances),
      },
      {
        path: 'documents',
        data: { permissions: ['DOCUMENT_VIEW'] },
        loadComponent: () => import('./pages/documents/documents').then((m) => m.Documents),
      },
      {
        path: 'maintenance/companies',
        data: { permissions: ['MAINTENANCE_VIEW'] },
        loadComponent: () =>
          import('./pages/maintenance/companies/maintenance-companies').then((m) => m.MaintenanceCompanies),
      },
      {
        path: 'maintenance',
        data: { permissions: ['MAINTENANCE_VIEW'] },
        loadComponent: () =>
          import('./pages/maintenance/maintenance').then((m) => m.Maintenance),
      },
      {
        path: 'inspections',
        data: { permissions: ['INSPECTION_VIEW'] },
        loadComponent: () =>
          import('./pages/inspections/inspections').then((m) => m.Inspections),
      },
      {
        path: 'calendar',
        data: { permissions: ['CALENDAR_VIEW'] },
        loadComponent: () => import('./pages/calendar/calendar').then((m) => m.Calendar),
      },
      {
        path: 'reports',
        data: { permissions: ['REPORT_VIEW'] },
        loadComponent: () => import('./pages/reports/reports').then((m) => m.Reports),
      },
      {
        path: 'operations',
        data: { permissions: ['REPORT_VIEW', 'PAYMENT_VIEW', 'OWNER_VIEW', 'ORG_VIEW'] },
        loadComponent: () => import('./pages/operations/operations').then((m) => m.Operations),
      },
      {
        path: 'subscription',
        data: { ownerOnly: true, permissions: ['SUBSCRIPTION_MANAGE'] },
        loadComponent: () =>
          import('./pages/subscription/subscription').then((m) => m.Subscription),
      },
      {
        path: 'payments/stripe-connect',
        data: { ownerOnly: true, permissions: ['PAYMENT_SETTINGS_MANAGE'] },
        loadComponent: () =>
          import('./pages/payments/stripe-connect/stripe-connect').then((m) => m.StripeConnect),
      },
      {
        path: 'communications/preview',
        canMatch: [() => !environment.production],
        data: { permissions: ['COMMUNICATION_VIEW'] },
        loadComponent: () => import('./pages/communications/preview/communications-preview').then((m) => m.CommunicationsPreview),
      },
      {
        path: 'communications',
        data: { permissions: ['COMMUNICATION_VIEW'] },
        loadComponent: () => import('./pages/communications/communications').then((m) => m.Communications),
      },
      {
        path: 'support',
        loadComponent: () => import('./pages/support/support').then((m) => m.Support),
      },
      {
        path: 'settings',
        data: { permissions: ['ORG_VIEW'] },
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./pages/settings/settings').then((m) => m.Settings),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
];
